import { merge, omitBy, isNil } from 'lodash-es';
import { appLocale, systemLib } from 'shared';

import type { BookingSuccessInfo } from '../analytics';
import { extractAnalyticsDataLib } from '../analytics-data-extraction';
import { collectBookingEventProperties } from '../analytics-events-storage';
import { bloomreachLib } from '../bloomreach';
import { sendAcceleraEvent } from './lib';
import {
  convertToAcceleraFormat,
  stringifyAllObjectsAndArrays,
} from './convert-to-accelera-format';

/**
 * booking_succeeded - Событие неудачного бронирования (5xx ошибка)
 * Отправляется с полем error при серверной ошибке
 */
export const bookingFailed = (errorMessage: string, phone?: string) => {
  const collectedProps = collectBookingEventProperties();

  const bookingData: Record<string, unknown> = {
    error: errorMessage,
    phone,
    platform: systemLib.getMobileOperatingSystem(),
    language: appLocale.lib.getAppLocale(),
  };

  const mappedData = merge({}, collectedProps, bookingData);

  convertToAcceleraFormat(mappedData);

  const cleanedData = omitBy(mappedData, (value, key) => {
    if (isNil(value)) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'string' && value === '') return true;
    if (key === 'connect_type' && String(value).toLowerCase() === 'none')
      return true;
    return false;
  });

  // Stringify nested objects for Accelera
  stringifyAllObjectsAndArrays(cleanedData);

  sendAcceleraEvent('booking_succeeded', cleanedData);

  // Clear events storage after booking failure to prevent data leaking to next booking
  bloomreachLib.resetEventsState();
};

/**
 * booking_succeeded - Событие успешного бронирования
 */
export const bookingSucceeded = (
  info: BookingSuccessInfo & { isAuthorized: boolean },
) => {
  // Collect properties from previous analytics events (stored in session storage)
  const collectedProps = collectBookingEventProperties();

  // Extract package and products
  const packageInfo = extractAnalyticsDataLib.package(
    info.packageInfo,
    info.products,
  );
  const productsNotInPackage = extractAnalyticsDataLib.products(
    info.products,
    info.medicalInsurance,
    info.roaming,
  );

  // Get package products
  const packageProducts = info.products
    ? Object.entries(info.products)
        .filter(
          ([, productState]) =>
            productState?.id && productState?.selectedAsPackage,
        )
        .map(([productName]) => productName)
    : undefined;

  // Create booking-specific data
  const bookingData: Record<string, unknown> = {
    is_authorized: info.isAuthorized,
    phone: info.phone,
    order_id: info.orderId,
    order_number: info.orderNumber,
    price: Number(info.total),
    ancillaries_baggage_count:
      extractAnalyticsDataLib.baggageCount(info.baggage) || 0,
    ancillaries_seat_map_count: extractAnalyticsDataLib.seatsCount(
      info.seatMap,
    ),
    package_type: packageInfo?.type,
    package_products: packageProducts?.length ? packageProducts : undefined,
    products: productsNotInPackage?.length ? productsNotInPackage : undefined,
    platform: systemLib.getMobileOperatingSystem(),
    language: appLocale.lib.getAppLocale(),
    // Override refundable from order response if provided (may be updated during booking flow)
    ...(info.refundable !== undefined && { refundable: info.refundable }),
    // Override segments data and baggage flags from current offer if provided
    // (may be updated after fare family change)
    // This ensures baggage/hand_luggage info and has_baggage/has_hand_luggage flags are up-to-date
    ...(info.segmentsData && {
      segments_data_from: info.segmentsData.segments_data_from,
      segments_data_to: info.segmentsData.segments_data_to,
      from_dep: info.segmentsData.from_dep,
      from_arr: info.segmentsData.from_arr,
      to_dep: info.segmentsData.to_dep,
      to_arr: info.segmentsData.to_arr,
      ...extractAnalyticsDataLib.getBaggageFlagsFromSegmentsData(
        info.segmentsData,
      ),
    }),
  };

  // Merge: collected props first, then booking data (booking data takes precedence)
  const mappedData = merge({}, collectedProps, bookingData);

  // Convert dates and times to accelera format (local times, not Unix)
  convertToAcceleraFormat(mappedData);

  // Clean up: remove empty arrays, null/undefined, and empty strings
  const cleanedData = omitBy(mappedData, (value, key) => {
    if (isNil(value)) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'string' && value === '') return true;
    if (key === 'connect_type' && String(value).toLowerCase() === 'none')
      return true;
    return false;
  });

  // Stringify all arrays for Accelera
  stringifyAllObjectsAndArrays(cleanedData);

  sendAcceleraEvent('booking_succeeded', cleanedData);

  // Clear events storage after booking to prevent data leaking to next booking
  // (e.g., date_to from RT flight appearing in OW booking)
  bloomreachLib.resetEventsState();
};
